import React, { useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../redux/modal.slice";
import Button from '../../components/Button';
import Tag from '../../components/Tag';
import QuestionsProgressBar from '../../components/QuestionsProgressBar';
import QuestionText from '../../components/QuestionText';
import AnswerOption from '../../components/AnswerOption';
import FinalScore from '../../components/FinalScore';
import { Circle } from '@untitledui/icons/Circle';
import { Check } from '@untitledui/icons/Check';
import { X } from '@untitledui/icons/X';
import { useCheckAnswerMutation } from '../../api/mutations/questions.mutation';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import Notification from '../../components/Notification';

const QuizQuestionsModal = ({ modalData }) => {
    const dispatch = useDispatch();
    const questions = useSelector((state) => state.questions.data);
    const [flashIndex, setFlashIndex] = useState(0);
    const [flipped, setFlipped] = useState(false);

    const handleCloseModal = () => dispatch(closeModal());

    const [currentIndex, setCurrentIndex] = useState(0);
    const [textAnswer, setTextAnswer] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [answerSubmitted, setAnswerSubmitted] = useState(false);
    const [isCorrect, setIsCorrect] = useState(null);
    const [explanation, setExplanation] = useState('');
    const [correctCount, setCorrectCount] = useState(0);
    const [showFinalScore, setShowFinalScore] = useState(false);

    const currentQuestion = questions[currentIndex];
    const total = questions.length;

    const { isPending, mutate } = useCheckAnswerMutation(currentQuestion.id);

    const handleSubmit = () => {
        const userAnswer =
            currentQuestion.question_type === 'multiple_choice'
                ? currentQuestion.metadata.options[selectedOption]?.text
                : textAnswer;

        mutate(
            { user_answer: userAnswer },
            {
                onSuccess: (res) => {
                    setAnswerSubmitted(true);
                    setIsCorrect(res.is_correct);
                    setExplanation(res.explanation);
                    if (res.is_correct) {
                        setCorrectCount(prev => prev + 1);
                    }
                },
                onError: () => {
                    toast.error(<Notification text={'Failed to submit answer'} icon={'warning'} />);
                },
            }
        );
    };

    const handleNext = () => {
        if (currentIndex < total - 1) {
            setCurrentIndex((prev) => prev + 1);
            setTextAnswer('');
            setSelectedOption(null);
            setAnswerSubmitted(false);
            setIsCorrect(null);
            setExplanation('');
        }
    };

    const isTextQuestion = currentQuestion.question_type === 'short_answer';
    const isFlashcardsMode = currentQuestion.question_type === 'flashcard';

    const canSubmit = isTextQuestion ? textAnswer.trim().length > 0 : selectedOption !== null

    const renderAnswerSection = () => {
      const { question_type, metadata } = currentQuestion;
    
      if (question_type === 'multiple_choice') {
        return (
          <div className="answers">
            {metadata.options.map((opt, idx) => {
              let className = '';
              let icon = <Circle size={14} />;
              const isUserChoice = idx === selectedOption;
              const isCorrectOption = opt.correct === true;
    
              if (answerSubmitted) {
                if (isUserChoice) {
                  className = isCorrect ? 'correct' : 'wrong';
                  icon = isCorrect ? <Check size={14} /> : <X size={14} />;
                }
                if (!isCorrect && isCorrectOption) {
                  className = 'correct';
                  icon = <Check size={14} />;
                }
              } else {
                if (selectedOption === idx) {
                  className = 'selected';
                }
              }
    
              return (
                <AnswerOption
                  key={idx}
                  id={`opt-${idx}`}
                  name={`answer-${currentIndex}`}
                  text={opt.text}
                  icon={icon}
                  className={className}
                  disabled={answerSubmitted || isPending}
                  checked={selectedOption === idx}
                  onChange={() => setSelectedOption(idx)}
                />
              );
            })}
          </div>
        );
      }
    
      if (question_type === 'short_answer') {
        return (
          <div className={`text-answer ${answerSubmitted ? (isCorrect ? 'correct' : 'wrong') : ''}`}>
            <input
              type="text"
              value={textAnswer}
              onChange={(e) => setTextAnswer(e.target.value)}
              placeholder="Type your answer..."
              className="formControl"
              disabled={answerSubmitted || isPending}
            />
            {answerSubmitted && (
              <div className="answer-feedback">
                {isCorrect ? (
                  <span className="text-success"><Check size={14} /> Correct!</span>
                ) : (
                  <span className="text-danger">
                    <X size={14} /> Wrong. {explanation && `Explanation: ${explanation}`}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      }
    
      if (isFlashcardsMode) {
        const q = questions[flashIndex];
        return (
          <div className="flashcard-practice">
            <div
              className={`flashcard ${flipped ? "flipped" : ""}`}
              onClick={() => setFlipped(!flipped)}
            >
              <div className="flashcard-front">
                <h6>Question</h6>
                <p>{q.metadata.question}</p>
              </div>
              <div className="flashcard-back">
                <h6>Answer</h6>
                <p>{q.metadata.answer}</p>
              </div>
            </div>

            <div className="flashcard-nav">
              <Button text="Prev" onClick={handlePrevFlash} disabled={flashIndex === 0} />
              <span>{flashIndex + 1} / {questions.length}</span>
              <Button text="Next" onClick={handleNextFlash} disabled={flashIndex === questions.length - 1} />
            </div>
          </div>
        );
      }
    
      return <div>Unsupported question type.</div>;
    };
      
    const handleNextFlash = () => {
      if (flashIndex < questions.length - 1) {
        setFlashIndex(prev => prev + 1);
        setFlipped(false);
      }
    };
    
    const handlePrevFlash = () => {
      if (flashIndex > 0) {
        setFlashIndex(prev => prev - 1);
        setFlipped(false);
      }
    };

    return (
        <>
            <div className='modal-header'>
                <h3 className='text-center mb-8'>Quiz Questions</h3>
                <div className='flex flex-align-center flex-justify-center flex-wrap'>
                    <Tag text={modalData?.categoryTitle} className="m-4" />
                    <Tag text={`${modalData?.num_questions} Questions`} className="m-4" />
                </div>
            </div>
            <div className="modal-body">
                {!showFinalScore ? 
                    <div className='questions-box'>
                        {!isFlashcardsMode &&
                            <>
                                <QuestionsProgressBar total={total} current={currentIndex + 1} />
                                <QuestionText
                                    number={currentIndex + 1}
                                    text={currentQuestion.metadata.question}
                                />
                            </>
                        }

                        {renderAnswerSection()}

                        {/* {answerSubmitted && explanation && (
                            <div className="answer-explanation mt-12">
                                <p><strong>Explanation:</strong> {explanation}</p>
                            </div>
                        )} */}
                    </div> :
                    <FinalScore 
                        totalQuestions={total}
                        correctAnswers={correctCount}
                    />
                }
            </div>
            <div className="modal-footer flex flex-justify-end">
                {!modalData?.testYourself &&
                  <Button
                      text={`${showFinalScore || isFlashcardsMode ? 'Close' : 'Cancel'}`}
                      type="outlined"
                      className='mr-12'
                      onClick={handleCloseModal}
                  />
                }
                {!showFinalScore && !isFlashcardsMode && (
                  !answerSubmitted ? (
                      <Button
                          text="Submit"
                          onClick={handleSubmit}
                          disabled={!canSubmit || isPending}
                      />
                  ) : currentIndex === total - 1 ? (
                      <Button
                          text="Final Score"
                          onClick={() => setShowFinalScore(true)}
                      />
                  ) : (
                      <Button
                          text="Next"
                          onClick={handleNext}
                      />
                  )
                )}
            </div>
            {isPending && <Loader />}
        </>
    );
};

export default QuizQuestionsModal;
